import { computed, effect, signal } from "@preact/signals";
import {
    AdminHistoryMode,
    EditorState,
    MakeAdminParams,
    PrepublishContent,
} from "./interfaces";
import { HelpTag } from "./tags";

export const makeAdmin = ({ api, closeHistory }: MakeAdminParams) => {
    const isAdmin = computed(() => api.service.enabled("Discovery", "Edit"));
    const isEditing = signal(false);
    const isGuide = signal(false);
    const isEditorHistory = signal(false);
    const defaultTab = signal(0);
    const editorTag = signal("");
    const stateStore = signal<EditorState[]>([]);
    const unsaved = computed(() => stateStore.value.filter((i) => i.dirty));
    const historyMode = signal<AdminHistoryMode>("list");
    const prepublishContent = signal<Record<string, PrepublishContent>>({});

    effect(() => {
        const items = api.discovery.items.value;
        const newState = stateStore.peek().slice();
        let changed = false;

        items.forEach((item) => {
            const idx = newState.findIndex((i) => i.tag === item.tag);

            if (idx === -1) {
                newState.push({
                    content: item.detailed,
                    parentContent: item.detailed,
                    detailedId: 0,
                    tag: item.tag as HelpTag,
                });
                changed = true;
            } else if (!newState[idx].touched) {
                if (newState[idx].content !== item.detailed) {
                    changed = true;
                }

                newState[idx].content = item.detailed;
                newState[idx].parentContent = item.detailed;
            }
        });

        if (changed) {
            stateStore.value = newState;
        }
    });

    effect(() => {
        if (stateStore.value.some((i) => i.dirty)) {
            window.onbeforeunload = () => "You have unsaved changes!";
        } else {
            window.onbeforeunload = null;
        }
    });

    const state = (tag: string) => {
        const item = stateStore.value.find((i) => i.tag === tag);
        const apiItem = api.discovery.items.value.find((i) => i.tag === tag);

        if (item) {
            return item;
        }

        const newItem = {
            content: apiItem?.detailed ?? "",
            parentContent: apiItem?.detailed ?? "",
            detailedId: 0,
            tag: tag as HelpTag,
            dirty: false,
            touched: false,
        };

        stateStore.value = [...stateStore.peek(), newItem];

        return newItem;
    };

    const openEditor = (tag?: string) => {
        if (!isAdmin.peek()) {
            return;
        }

        closeHistory();

        isEditing.value = true;

        if (tag) {
            const isFallback = tag.at(-1) === "?";
            const finalTag = isFallback
                ? tag.substring(0, tag.length - 1)
                : tag;

            editorTag.value = finalTag;
            defaultTab.value = isFallback ? 1 : 0;
        }
    };

    const closeEditor = (cleanup = false) => {
        isEditing.value = false;

        if (cleanup) {
            stateStore.value = stateStore.peek().map((i) => ({
                ...i,
                dirty: false,
            }));
        }
    };

    const selectRevision = async (tag: HelpTag, detailedId: number) => {
        const newState = stateStore.peek().slice();
        const idx = newState.findIndex((i) => i.tag === tag);

        // history should be loaded by the time this function is called
        const history = api.discovery.getHistory(tag).peek();
        const newContent = await api.discovery.applyDiff(history, detailedId);

        if (idx === -1) {
            newState.push({
                content: newContent,
                parentContent: newContent,
                detailedId,
                tag,
            });
        } else {
            newState[idx].content = newContent;
            newState[idx].parentContent = newContent;
            newState[idx].detailedId = detailedId;
            newState[idx].touched = false;
            newState[idx].dirty = false;
        }

        stateStore.value = newState;
    };

    const edit = (tag: HelpTag, value: string) => {
        const newState = stateStore.peek().slice();
        const idx = newState.findIndex((i) => i.tag === tag);

        if (idx === -1) {
            const apiItem = api.discovery.items.value.find(
                (i) => i.tag === tag,
            );

            newState.push({
                content: value,
                parentContent: apiItem?.detailed ?? "",
                detailedId: 0,
                tag,
                dirty: true,
                touched: true,
            });
        } else {
            newState[idx].content = value;
            newState[idx].dirty = true;
            newState[idx].touched = true;
        }

        stateStore.value = newState;
    };

    const saveAll = async (lang: string) => {
        // @TODO: tbd
        void lang;
    };

    const actions = (tag: string) => {
        const s = state(tag);
        const item = api.discovery.items.value.find((i) => i.tag === tag);
        const history = api.discovery.getHistory(tag).value;
        const loaded =
            api.discovery.loaded(tag).value &&
            api.discovery.historyLoaded(tag).value;

        if (!loaded) {
            // item has not been loaded yet - no actions allowed
            return { create: false, publish: false, state: s };
        }

        if (history.length === 0 || !item) {
            // first revision - create & publish
            return {
                init: true,
                create: Boolean(s.content),
                publish: Boolean(s.content),
                state: s,
            };
        }

        const create = s.parentContent !== s.content;
        const publish =
            s.parentContent !== item.detailed && s.parentContent === s.content;

        return { create, publish, state: s };
    };

    const save = async (tag: string, lang: string) => {
        const a = actions(tag);

        if (a.create) {
            const newId = await api.discovery.updateTagDetails({
                tag,
                lang,
                details: a.state.content,
                detailedId: a.state.detailedId,
                activate: a.publish,
            });

            const newState = stateStore.peek().slice();
            const idx = newState.findIndex((i) => i.tag === tag);
            const history = api.discovery.getHistory(tag).peek();

            if (idx !== -1) {
                const newContent = await api.discovery.applyDiff(
                    history,
                    newId,
                );

                newState[idx].detailedId = newId;
                newState[idx].dirty = false;
                newState[idx].touched = false;
                newState[idx].content = newContent;
                newState[idx].parentContent = newContent;
                stateStore.value = newState;
            }
        } else if (a.publish) {
            await api.discovery.setActiveTagDetails({
                tag,
                detailedId: a.state.detailedId,
            });
        }

        prepublish(tag, false);

        return false;
    };

    const prepublish = (tag: string, active: boolean) => {
        const s = state(tag);
        const newContent = { ...prepublishContent.peek() };

        if (active) {
            newContent[tag] = {
                content: s.content,
                detailedId: s.detailedId,
            };
        } else {
            delete newContent[tag];
        }

        prepublishContent.value = newContent;
    };

    const getPrepublishContent = (tag: string) => {
        const content = prepublishContent.value;

        if (tag in content) {
            return content[tag];
        }

        return null;
    };

    return {
        unsaved,
        isAdmin,
        isGuide,
        isEditing,
        isEditorHistory,
        defaultTab,
        editorTag,
        historyMode,
        state,
        openEditor,
        closeEditor,
        selectRevision,
        edit,
        saveAll,
        actions,
        save,
        prepublish,
        getPrepublishContent,
    };
};
